import { useMutation } from "@tanstack/react-query";

import {
  caaCustomerAddressCreateMutation,
  caaCustomerAddressDeleteMutation,
  caaCustomerAddressUpdateMutation,
  caaCustomerEmailMarketingSubscribeMutation,
  caaCustomerEmailMarketingUnsubscribeMutation,
  caaCustomerQuery,
  caaCustomerUpdateMutation,
} from "../graphql/customer-account-gql";
import { locale } from "../locales";
import type {
  ICaaCustomerAddressCreateMutation,
  ICaaCustomerAddressDeleteMutation,
  ICaaCustomerAddressUpdateMutation,
  ICaaCustomerEmailMarketingSubscribeMutation,
  ICaaCustomerEmailMarketingUnsubscribeMutation,
  ICaaCustomerQuery,
  ICaaCustomerUpdateMutation,
  ICaaUserError,
} from "../types/api/customer-account";
import type { IToast } from "../types/domain/layout";
import { ToastType } from "../types/enum";
import type { TCaaRequestFn } from "../types/graphql";

interface IUseCustomerQueryCaaProps {
  request: TCaaRequestFn;
  lang: "ko" | "en" | "jp";
  openToast: (toast: IToast) => void;
}

/**
 * Customer Account API customer 쿼리/뮤테이션 바인딩
 */
export default function useCustomerQueryCaa({ request, lang, openToast }: IUseCustomerQueryCaaProps) {
  const notifyUserErrors = (userErrors: ICaaUserError[]) => {
    userErrors.forEach((error) => {
      const messages: Record<string, string> = locale(lang).customer_error;
      openToast({ type: ToastType.Error, message: (error.code && messages[error.code]) || messages.DEFAULT });
    });
  };

  const fetchCaaCustomerMutation = useMutation({
    mutationFn: () => request<ICaaCustomerQuery>(caaCustomerQuery),
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const updateCaaCustomerMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<ICaaCustomerUpdateMutation>(caaCustomerUpdateMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerUpdate.userErrors);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const createCaaAddressMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerAddressCreateMutation>(caaCustomerAddressCreateMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerAddressCreate.userErrors);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const editCaaAddressMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerAddressUpdateMutation>(caaCustomerAddressUpdateMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerAddressUpdate.userErrors);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const deleteCaaAddressMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerAddressDeleteMutation>(caaCustomerAddressDeleteMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerAddressDelete.userErrors);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const subscribeCaaEmailMarketingMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerEmailMarketingSubscribeMutation>(caaCustomerEmailMarketingSubscribeMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerEmailMarketingSubscribe.userErrors);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const unsubscribeCaaEmailMarketingMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerEmailMarketingUnsubscribeMutation>(caaCustomerEmailMarketingUnsubscribeMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerEmailMarketingUnsubscribe.userErrors);
    },
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  return {
    fetchCaaCustomer: fetchCaaCustomerMutation.mutateAsync,
    fetchCaaCustomerLoading: fetchCaaCustomerMutation.isPending,
    updateCaaCustomer: updateCaaCustomerMutation.mutateAsync,
    updateCaaCustomerLoading: updateCaaCustomerMutation.isPending,
    createCaaAddress: createCaaAddressMutation.mutateAsync,
    createCaaAddressLoading: createCaaAddressMutation.isPending,
    editCaaAddress: editCaaAddressMutation.mutateAsync,
    editCaaAddressLoading: editCaaAddressMutation.isPending,
    deleteCaaAddress: deleteCaaAddressMutation.mutateAsync,
    deleteCaaAddressLoading: deleteCaaAddressMutation.isPending,
    subscribeCaaEmailMarketing: subscribeCaaEmailMarketingMutation.mutateAsync,
    subscribeCaaEmailMarketingLoading: subscribeCaaEmailMarketingMutation.isPending,
    unsubscribeCaaEmailMarketing: unsubscribeCaaEmailMarketingMutation.mutateAsync,
    unsubscribeCaaEmailMarketingLoading: unsubscribeCaaEmailMarketingMutation.isPending,
  };
}
